import Head from "next/head";
import React from "react";
import { ExtendedNextPage } from "types/pages";

import HummingNav from "pages/components/HummingNav";
import HummingFooter from "pages/components/HummingFooter";
import Label from "components/Label";
import Content from "components/Content";

import styles from "./styles.module.scss";
import { getLocations } from "utils/content/services";
import { GetStaticProps } from "next";
import { HowItWorks, YourMembership } from "pages/homepage/components";
import TheDifference from "pages/homepage/components/TheDifference";
import getStateFullName from "utils/getFullStateName";
import Header from "pages/blog/components/Header";

export type Location = {
  title: string;
  slug: string;
  state: string;
};

export interface LocationData extends Location {
  fields: Location;
}

function formattedCityName(inputString: string): string {
  if (inputString.includes(",")) {
    const parts = inputString.split(", ");
    return parts[0];
  }
  return inputString;
}

function groupByStateAndStrip(entries: Location[]): Record<string, Location[]> {
  const stateGroups: Record<string, Location[]> = {};

  for (const entry of entries) {
    const { state } = entry;

    const stateFullTitle = getStateFullName(state);

    if (!stateGroups[stateFullTitle]) {
      stateGroups[stateFullTitle] = [];
    }

    // Extract and include only the "fields" information in the result
    stateGroups[stateFullTitle].push(entry);
  }

  return stateGroups;
}

const LocationsPage: ExtendedNextPage<{ locationsData: Location[] }> = ({
  locationsData
}) => {
  const cities = groupByStateAndStrip(locationsData);
  const states = cities && Object.keys(cities);

  return (
    <div className={styles.container}>
      <Head>
        <title>Services Offered Through Humming Homes</title>
        <meta
          name="description"
          content="Your source for all things tied to your home. From property management advice to proactive maintenance to city guides, we've got you covered."
        />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Header title="Services Offered Through Humming Homes" />
      <Content
        contentClassName={styles.locationsContainer}
        containerClassName={styles.locationsWrapper}
      >
        <div className={styles.locationsList}>
          {states &&
            states.map((state) => (
              <div className={styles.stateContainer}>
                <Label variant="h4" className={styles.title} color="primary">
                  {state}
                </Label>
                <div className={styles.citiesList}>
                  {cities &&
                    cities[state].map((city) => (
                      <a href={`/services/${city.slug}`}>
                        <Label variant="md" color="primary">
                          {formattedCityName(city.title)}
                        </Label>
                      </a>
                    ))}
                </div>
              </div>
            ))}
        </div>
      </Content>

      <HowItWorks />
      <YourMembership />
      <TheDifference />
    </div>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const locations = await getLocations();
  const locationsData = locations.items.map(
    (location: LocationData) => location.fields
  );
  return {
    props: { locationsData }
  };
};

LocationsPage.getLayout = (page) => {
  return (
    <div>
      <HummingNav />
      {page}
      <HummingFooter />
    </div>
  );
};

export default LocationsPage;
